/**
 * Folding a record page's chrome away — `property-maps-v2.md` §7.
 *
 * On an iPad in landscape there is room for the desktop layout and a finger
 * to use it with, and the two disagree: the Property header, its pills and
 * the tab strip are about 250px of a 820px-tall window, and a map is the one
 * tab whose whole job is the room. So tapping the canvas or picking a drawing
 * tool folds all of that into a single 44px bar — `‹ Riverfront Office Park ·
 * Map` — and `‹` puts it back.
 *
 * **The tab asks; the page does it.** The fold is state the *content* owns
 * (only the map knows a tool was picked) over chrome the *page* owns, so it
 * travels as a context rather than as a prop: `RecordPage` provides it and
 * swaps its header and tab strip for the slim bar, and any tab's content can
 * call `fold()`. Threading a callback through `children(tab)` would change
 * the signature every record config is written against for the sake of one
 * tab on one record.
 *
 * A record page with no provider — a tab rendered on its own in a test, the
 * phone's full-screen editor — gets a `fold` that does nothing, which is the
 * right answer rather than a crash: there is no chrome there to fold.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export interface RecordChrome {
  folded: boolean
  fold: () => void
  unfold: () => void
}

const NONE: RecordChrome = { folded: false, fold: () => {}, unfold: () => {} }

const Context = createContext<RecordChrome>(NONE)

export const useRecordChrome = (): RecordChrome => useContext(Context)

export function RecordChromeProvider({ children }: { children: ReactNode }) {
  const [folded, setFolded] = useState(false)
  /*
   * `fold` and `unfold` never change identity, which is what lets the map
   * unfold the page on its way out of the Map tab: an effect whose cleanup
   * calls `unfold` must not re-run every time `folded` flips, or folding
   * would immediately undo itself.
   */
  const fold = useCallback(() => setFolded(true), [])
  const unfold = useCallback(() => setFolded(false), [])
  const value = useMemo<RecordChrome>(() => ({ folded, fold, unfold }), [folded, fold, unfold])
  return <Context.Provider value={value}>{children}</Context.Provider>
}
